/**
 * Domain enums. These mirror the Postgres enum types one-for-one so the UI copy,
 * sort orders and badge variants carried over from the Supabase build still apply.
 */

export const APP_ROLES = [
  "super_admin",
  "somasi_owner",
  "somasi_manager",
  "somasi_team",
  "outsourced_admin",
  "outsourced_team",
  "readonly",
] as const;
export type AppRole = (typeof APP_ROLES)[number];

export const COMPANY_STATUSES = [
  "active",
  "dormant",
  "struck_off",
  "dissolved",
  "in_liquidation",
  "closed",
] as const;
export type CompanyStatus = (typeof COMPANY_STATUSES)[number];

export const CLIENT_GROUP_TYPES = [
  "direct",
  "b2b_corporate",
  "intermediary",
  "individual",
] as const;
export type ClientGroupType = (typeof CLIENT_GROUP_TYPES)[number];

export const CONTACT_ROLE_TYPES = [
  "primary",
  "authorised_person",
  "director",
  "shareholder",
  "beneficial_owner",
  "billing",
  "accounts",
  "afr",
  "compliance",
  "operations",
  "payroll",
  "audit",
  "legal",
  "corporate_secretarial",
  "document_recipient",
  "invoice_recipient",
  "payment_follow_up",
  "other",
] as const;
export type ContactRoleType = (typeof CONTACT_ROLE_TYPES)[number];

export const BILLING_FREQUENCIES = [
  "one_time",
  "monthly",
  "quarterly",
  "half_yearly",
  "annual",
  "per_transaction",
  "per_employee",
  "per_hour",
  "per_document",
  "per_company",
  "custom",
] as const;
export type BillingFrequency = (typeof BILLING_FREQUENCIES)[number];

export const BILLING_BASES = [
  "fixed",
  "variable",
  "reimbursement_at_cost",
  "reimbursement_with_markup",
] as const;
export type BillingBasis = (typeof BILLING_BASES)[number];

/**
 * Retired. A fee schedule no longer carries a scope.
 *
 * It used to say whether a schedule priced everybody, one client group or one
 * company, and carried the target alongside. That made the relationship
 * bidirectional — the schedule named its target and the target named its
 * schedule — so the two could disagree, and keeping them in step took an
 * activation hand-over of three writes plus a one-active-per-target rule.
 *
 * Only the target names its schedule now (`companies.fee_schedule_id`,
 * `client_groups.fee_schedule_id`). A company points at exactly one schedule, so
 * the invariant that rule enforced is structural rather than policed. These
 * values exist only so a stored row written under one still renders a label
 * instead of `undefined`.
 */
export const LEGACY_FEE_SCHEDULE_TYPES = [
  "somasi_standard",
  "client_group",
  "company_specific",
  "b2b_client",
  "direct_client",
  "special_contract",
  "historic",
] as const;
export type LegacyFeeScheduleType = (typeof LEGACY_FEE_SCHEDULE_TYPES)[number];

export const COMPANY_SERVICE_STATUSES = ["active", "paused", "discontinued"] as const;
export type CompanyServiceStatus = (typeof COMPANY_SERVICE_STATUSES)[number];

/**
 * Which levels of the cascade a company reads.
 *
 * The values are unchanged so stored rows keep their meaning, but one of them
 * has narrowed: there is no published house price list any more, so
 * `somasi_standard` now means the service catalogue's own `default_fee` and
 * nothing above it.
 */
export const PRICING_MODES = [
  "somasi_standard",
  "client_group",
  "company_specific",
  "manual",
] as const;
export type PricingMode = (typeof PRICING_MODES)[number];

export const FEE_OVERRIDE_REASONS = [
  "special_client_agreement",
  "discount_approved",
  "additional_work",
  "urgent_service",
  "fee_waiver",
  "partial_service",
  "prior_billing_adjustment",
  "management_instruction",
  "correction",
  "other",
] as const;
export type FeeOverrideReason = (typeof FEE_OVERRIDE_REASONS)[number];

export const APPROVAL_STATUSES = ["not_required", "pending", "approved", "rejected"] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

export const INVOICE_STATUSES = ["draft", "sent", "paid", "partially_paid", "cancelled"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

/**
 * The VIRRGIN account is a float Somasi holds with the BVI government: money is
 * paid in, and registry fees are drawn down against it. A `deposit` is a top-up
 * Somasi makes, a `charge` is a fee the registry takes out — so the balance is
 * the deposits less the charges. Only charges are ever billed to a client.
 */
export const VIRRGIN_TXN_TYPES = ["charge", "deposit"] as const;
export type VirrginTxnType = (typeof VIRRGIN_TXN_TYPES)[number];

// ---------------------------------------------------------------------------
// Payroll, leave and attendance
// ---------------------------------------------------------------------------

/**
 * How often an employee is paid, which fixes how many periods the year holds.
 * `PERIODS_PER_YEAR` in `payroll.ts` is the mapping; this is the vocabulary.
 */
export const PAY_FREQUENCIES = ["monthly", "semi_monthly", "fortnightly", "weekly"] as const;
export type PayFrequency = (typeof PAY_FREQUENCIES)[number];

export const EMPLOYMENT_TYPES = [
  "full_time",
  "part_time",
  "fixed_term",
  "contractor",
  /**
   * A director, partner or active shareholder. Payroll tax treats them as
   * employed whether or not they draw a salary, and they keep no attendance.
   */
  "deemed_employee",
] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

export const EMPLOYEE_STATUSES = ["active", "on_leave", "suspended", "left"] as const;
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

/**
 * What one half of one day was.
 *
 * The first six are worked time and cost nothing; `leave` defers to the leave
 * type's own `paid` flag; `absent_unauthorised` is always unpaid. `unconfirmed`
 * is the one that matters most — it is what a working day with no evidence
 * becomes, and it is never treated as an absence. A period cannot close while
 * any half-day in it is still unconfirmed, which is what turns "we did not know"
 * into a question someone answers rather than a deduction someone discovers.
 */
export const ATTENDANCE_STATUSES = [
  "office",
  "wfh",
  "client_site",
  "travel",
  "training",
  "on_call",
  "leave",
  "holiday",
  "non_working",
  "absent_unauthorised",
  "unconfirmed",
] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];

/** Statuses that are worked time: present, paid, and never an absence. */
export const WORKING_ATTENDANCE_STATUSES: readonly AttendanceStatus[] = [
  "office",
  "wfh",
  "client_site",
  "travel",
  "training",
  "on_call",
];

/** Statuses where the calendar or the contract says no work was due. */
export const NON_WORKING_ATTENDANCE_STATUSES: readonly AttendanceStatus[] = [
  "holiday",
  "non_working",
];

/** Which half of a day a record covers. */
export const DAY_HALVES = ["am", "pm"] as const;
export type DayHalf = (typeof DAY_HALVES)[number];

/**
 * Where a day's record came from, so a disputed deduction can be traced.
 *
 * `auto` is the rollup's own proposal from login evidence. It is never the
 * source of a day that costs anybody money: `absent_unauthorised` can only be
 * reached by `manager`.
 */
export const ATTENDANCE_SOURCES = ["auto", "self", "manager", "import", "schedule"] as const;
export type AttendanceSource = (typeof ATTENDANCE_SOURCES)[number];

export const LEAVE_REQUEST_STATUSES = [
  "draft",
  "pending",
  "approved",
  "rejected",
  "cancelled",
] as const;
export type LeaveRequestStatus = (typeof LEAVE_REQUEST_STATUSES)[number];

/**
 * How a leave type is paid.
 *
 *  - `full`     — the day costs the employee nothing.
 *  - `unpaid`   — the day is deducted at the half-day rate.
 *  - `partial`  — paid at `paid_percentage`, the balance deducted. Statutory
 *                 maternity in several territories works this way, and sickness
 *                 beyond an employer's own limit sometimes does.
 */
export const LEAVE_PAY_TREATMENTS = ["full", "unpaid", "partial"] as const;
export type LeavePayTreatment = (typeof LEAVE_PAY_TREATMENTS)[number];

/** What happens to an unused balance when the leave year turns over. */
export const CARRY_OVER_RULES = ["none", "capped", "unlimited"] as const;
export type CarryOverRule = (typeof CARRY_OVER_RULES)[number];

/**
 * The life of a pay period.
 *
 * `approved` onwards is locked: the payslips in it are a stored snapshot and no
 * attendance, rate or contract change reaches back into them. A correction found
 * later lands as an adjustment on the next open period, which is the only way a
 * filed return and the system can continue to agree.
 */
export const PAY_PERIOD_STATUSES = [
  "open",
  "calculating",
  "review",
  "approved",
  "paid",
  "filed",
] as const;
export type PayPeriodStatus = (typeof PAY_PERIOD_STATUSES)[number];

/**
 * Where one financial year's Annual Financial Return stands.
 *
 * `pending` is the only state a newly rolled period can be in — the roll creates
 * it with no filing date, which is what "open" means here. `filed` is set by the
 * fill operation together with the date, never on its own: a status that says
 * filed with no date behind it is the exact ambiguity the single-column model
 * had. `waived` is for a year the registry did not require a return for, which
 * closes the period without inventing a filing date for it.
 */
export const AFR_STATUSES = ["pending", "filed", "waived"] as const;
export type AfrStatus = (typeof AFR_STATUSES)[number];

/**
 * How a financial year's label is written out.
 *
 * House style, not a property of any jurisdiction — see `afr.ts` for why the
 * *format* (one year or two) is derived from the period's own dates and only the
 * punctuation is configurable.
 */
export const FY_LABEL_FORMATS = ["short_dash", "long_dash", "slash"] as const;
export type FyLabelFormat = (typeof FY_LABEL_FORMATS)[number];

/** The authority a filing is made to. */
export const FILING_AUTHORITIES = ["payroll_tax", "nhi", "ssb"] as const;
export type FilingAuthority = (typeof FILING_AUTHORITIES)[number];

export const FILING_STATUSES = ["due", "filed", "paid", "overdue"] as const;
export type FilingStatus = (typeof FILING_STATUSES)[number];

/**
 * What a file attached to a statutory return is.
 *
 * A return leaves two pieces of paper behind and they answer different
 * questions: the form that was submitted says what was declared, the receipt
 * says the money actually left. Keeping them as one kind called "attachment"
 * means the list cannot tell you whether a return was paid without somebody
 * opening every file on it.
 *
 * `acknowledgement` is the authority's own confirmation of receipt, which in
 * BVI arrives separately from the payment and is the only proof of filing date
 * that is not the employer's own word.
 */
export const FILING_DOCUMENT_KINDS = [
  "return",
  "payment_receipt",
  "acknowledgement",
  "correspondence",
  "other",
] as const;
export type FilingDocumentKind = (typeof FILING_DOCUMENT_KINDS)[number];

/**
 * The kinds an AFR starts with — seeds for `afr_document_kinds`, not a closed set.
 *
 * Not the payroll return's {@link FILING_DOCUMENT_KINDS}, though the storage
 * underneath is shared. An AFR leaves different paper behind: the financial
 * statements are the substance of the filing rather than an attachment to it,
 * and there is no payment receipt because the return is not a remittance.
 *
 * Written as the labels they display, because that is what a document stores —
 * and because somebody adding a sixth kind types a label, not a slug.
 */
export const AFR_DOCUMENT_KINDS = [
  "Financial statements",
  "Filed return",
  "Registry acknowledgement",
  "Correspondence",
  "Other",
] as const;

/** Whether a presence signal came from a network the employer recognises. */
export const PRESENCE_MATCHES = ["office", "known_remote", "unknown"] as const;
export type PresenceMatch = (typeof PRESENCE_MATCHES)[number];

/**
 * What a clock-in amounted to.
 *
 * `counted` is the ordinary outcome: somebody clocked in on a day they were due
 * to work, so the hours count. The button is never refused, because refusing it
 * loses the fact that they were working and tells them nothing useful.
 *
 * `pending` is the one case the system will not settle for itself: clocked in
 * outside the working pattern, or on a public holiday. It is a question for a
 * manager, not a refusal and not an absence — exactly as an unanswered day is.
 */
export const TIME_ENTRY_STATUSES = ["counted", "pending", "rejected", "open"] as const;
export type TimeEntryStatus = (typeof TIME_ENTRY_STATUSES)[number];

/**
 * What an amendment to a clocked time is at — a correction to a clock-in or a
 * clock-out that was wrong, late or never made.
 *
 * `applied` rather than `approved`, because the two are not the same event
 * here. A correction inside somebody's own monthly allowance is applied the
 * moment they make it — there was nothing to approve — and one beyond it is
 * `pending` until an admin decides, at which point approving it *is* applying
 * it. One status for "the entry now says this" keeps the history honest about
 * which corrections actually took effect.
 */
export const TIME_AMENDMENT_STATUSES = ["pending", "applied", "rejected", "cancelled"] as const;
export type TimeAmendmentStatus = (typeof TIME_AMENDMENT_STATUSES)[number];

/**
 * Why a clock-in counted, or did not. Printed back to whoever clocked in.
 *
 * `office_network`, `known_remote_network` and `unrecognised_network` are no
 * longer produced: where a clock-in came from stopped deciding whether it
 * counted. They stay in the vocabulary because entries recorded while it did
 * carry them, and a stored value that is not in the list is a row the UI cannot
 * describe.
 */
export const CLOCK_VERDICTS = [
  "clocked_in",
  "work_from_home_approved",
  "no_working_day",
  "office_network",
  "known_remote_network",
  "unrecognised_network",
] as const;
export type ClockVerdict = (typeof CLOCK_VERDICTS)[number];

/**
 * What a document attached to an employee is.
 *
 * Vocabulary rather than policy, so an enum like the contact roles — but
 * `other` carries a free-text label, because the one kind a fixed list always
 * misses is the one a particular jurisdiction asks for.
 *
 * `work_permit` matters more than the rest here: BVI requires one for a foreign
 * employee, it expires, and letting it lapse is an employer's problem rather
 * than an employee's.
 */
export const EMPLOYEE_DOCUMENT_KINDS = [
  "employment_contract",
  "offer_letter",
  "work_permit",
  "immigration",
  "identification",
  "qualification",
  "medical_certificate",
  "tax_form",
  "bank_mandate",
  "performance_review",
  "disciplinary",
  "resignation",
  "other",
] as const;
export type EmployeeDocumentKind = (typeof EMPLOYEE_DOCUMENT_KINDS)[number];
